<template>
  <v-card
    v-if="calculator || modified"
    :max-width="_getMaxWidth()"
    :class="{ 'vetalfa-view': !modified }"
    elevation="0"
  >
    <div class="vetalfa-global-header" ref="screenHeader" v-if="!componentMode">
      <vetalfa-small-header
        showBreadCrumbAndTitle
        :breadcrumb="!!calculator ? calculator.name : null"
        :title="category ? category.portugueseName : null"
        hasHeaderOptions
      ></vetalfa-small-header>
      <v-row no-gutters justify="center">
        <vetalfa-drugs-header
          :name="calculator.name"
          :description="calculator.classes[0]"
          :displayCalculator="false"
          class="vetalfa-max-width my-4 px-2"
        >
        </vetalfa-drugs-header>
      </v-row>
    </div>
    <div ref="screenContent" style="height: 100%">
      <vetalfa-section-title
        class="text-left"
        :prop="'Peso do paciente (kg)'"
        :modified="modified"
      >
        <v-text-field
          v-bind:id="`numeric-weight`"
          type="text"
          hide-details
          inputmode="decimal"
          suffix="(kg)"
          class="mx-2 my-0 mb-5"
          light
          v-model="weight"
          dense
          color="primary"
          outlined
          placeholder="Insira o peso do paciente"
        ></v-text-field>
      </vetalfa-section-title>
      <vetalfa-section-title
        class="text-left"
        :prop="'Dose por quilograma (kg)'"
        :modified="modified"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="7" class="ma-0 pa-0 px-2 mb-5">
            <v-text-field
              v-bind:id="`numeric-doseForWeight`"
              type="text"
              hide-details
              inputmode="decimal"
              light
              v-model="doseForWeight"
              dense
              color="primary"
              outlined
              placeholder="Insira a dose por kg"
            ></v-text-field>
          </v-col>
          <v-col cols="5" class="ma-0 pa-0 pr-2 mb-5">
            <v-select
              :items="['g/kg', 'mg/kg', 'mcg/kg', 'UI/kg', 'mL/kg']"
              hide-details
              placeholder="Selecione"
              filled
              dense
              outlined
              v-model="doseForWeightUnit"
            ></v-select>
          </v-col>
        </v-row>
      </vetalfa-section-title>
      <vetalfa-section-title
        class="text-left"
        v-if="doseForWeightUnit != 'mL/kg'"
        :prop="'Concentração do Medicamento'"
        :modified="modified"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="7" class="ma-0 pa-0 px-2 mb-5">
            <v-text-field
              v-bind:id="`numeric-concentration`"
              type="text"
              hide-details
              inputmode="decimal"
              light
              v-model="concentration"
              dense
              color="primary"
              outlined
              placeholder="Insira a concentração"
            ></v-text-field>
          </v-col>
          <v-col cols="5" class="ma-0 pa-0 pr-2 mb-5">
            <v-select
              :items="concentrationItems"
              placeholder="Selecione"
              hide-details
              filled
              dense
              outlined
              v-model="concentrationUnit"
            ></v-select>
          </v-col>
        </v-row>
      </vetalfa-section-title>
      <vetalfa-section-title
        v-if="response"
        prop="Resultados"
        :modified="modified"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <vetalfa-section-item prop="Dose Total"></vetalfa-section-item>
            <vetalfa-section-sub-item
              class="ml-5 pl-5 pr-3"
              :prop="response.totalDose"
            ></vetalfa-section-sub-item>
            <vetalfa-section-item
              prop="Volume do Medicamento"
            ></vetalfa-section-item>
            <vetalfa-section-sub-item
              class="ml-5 pl-5 pr-3"
              :prop="response.volume"
            ></vetalfa-section-sub-item>
          </v-col>
        </v-row>
      </vetalfa-section-title>

      <vetalfa-general-tab
        v-if="!componentMode"
        :collapseAll="true"
        :sections="collapsedSections"
        :references="calculator.references"
      ></vetalfa-general-tab>
      <div
        v-if="!componentMode"
        style="height: 70px; background-color: white"
      ></div>
    </div>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import calculatorLabels from "@/constants/labels/calculators.labels.js";
import labels from "@/constants/labels.js";
export default {
  name: "DoseCalculator",
  props: {
    componentMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    modified: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters("drugs", ["category"]),
    ...mapGetters("calculators", ["calculator"]),
    ...mapGetters("patients", ["patient"]),
    collapsedSections() {
      let arr = [];
      this.calculator.sections.forEach((s) => {
        arr.push({ ...s, collapse: true });
      });
      return arr;
    },
    concentrationItems() {
      if (this.doseForWeightUnit == "UI/kg") {
        return [{ header: "Solução | Líquido" }, "UI/mL"];
      }
      return [
        { header: "Solução | Líquido" },
        "%",
        "g/mL",
        "mg/mL",
        "mcg/mL",
        {
          header: "Apresentação Sólida/Pastosa (Comprimido, Cápsula, Tablete)",
        },
        "g/apresentação",
        "mg/apresentação",
        "mcg/apresentação",
      ];
    },
  },
  methods: {
    ...mapActions("calculators", ["openCalculatorById"]),
    parseNumberField(e) {
      const id = e.srcElement.id.replace("numeric-", "");
      // Não transforme entradas inválidas (por exemplo, -5 ou 5abc) em doses
      // positivas. Preserve o texto para correção e invalide o resultado.
      if (!/^\d*(?:[.,]\d*)?$/.test(e.srcElement.value)) {
        this[id] = e.srcElement.value;
        return;
      }
      const { value } = this._formatNumberField(e, this[id]);
      this[id] = value;
    },
    positiveNumber(value) {
      const text = String(value == null ? "" : value).trim();
      if (!/^\d+(?:[.,]\d+)?$/.test(text)) return null;
      const number = Number(this._getNumber(text));
      return Number.isFinite(number) && number > 0 ? number : null;
    },
    calculateResults() {
      const weight = this.positiveNumber(this.weight);
      const doseForWeight = this.positiveNumber(this.doseForWeight);
      const concentration = this.positiveNumber(this.concentration);
      this.response = null;
      if (
        weight !== null &&
        doseForWeight !== null &&
        !!this.doseForWeightUnit &&
        (this.doseForWeightUnit == "mL/kg" ||
          (concentration !== null && !!this.concentrationUnit))
      ) {
        let response = {};
        let totalDose = weight * doseForWeight;
        if (!Number.isFinite(totalDose) || totalDose <= 0) return;
        response["totalDose"] = this._formatAndRoundNumber(totalDose);
        let doseUnit = this.doseForWeightUnit.replace("/kg", "");
        response["totalDose"] = `${response["totalDose"]} ${doseUnit}`;
        if (doseUnit == "mL") {
          response["volume"] = `${this._formatAndRoundNumber(
            totalDose
          )} ${doseUnit}`;
        } else {
          const conversion = {
            "%": 0.01,
            g: 1,
            mg: 0.001,
            mcg: 0.000001,
            UI: 1,
          };
          let concentrationUnit = this.concentrationUnit.split("/")[0];
          let volume = totalDose * conversion[doseUnit];
          volume = volume / concentration;
          volume = volume / conversion[concentrationUnit];
          if (!Number.isFinite(volume) || volume <= 0) return;
          let volumeUnit = "mL";
          if (this.concentrationUnit.includes("apresentação")) {
            volumeUnit =
              volume == 1
                ? "unidade de apresentação"
                : "unidades de apresentação";
          }
          response["volume"] = `${this._formatAndRoundNumber(
            volume
          )} ${volumeUnit}`;
        }
        this.response = response;
        this.$emit("response");
      }
    },
    setupVariables() {
      ["weight", "doseForWeight", "concentration"].forEach((n) => {
        let el = document.getElementById(`numeric-${n}`);
        if (!el) return;
        el.value = this[n] || "";
        el.addEventListener("input", this.parseNumberField);
        el.addEventListener("focus", this.parseNumberField);
        el.addEventListener("blur", this.parseNumberField);
      });
    },
  },
  watch: {
    weight() {
      this.calculateResults();
    },
    doseForWeightUnit() {
      this.concentrationUnit = null;
      this.calculateResults();
    },
    doseForWeight() {
      this.calculateResults();
    },
    concentrationUnit() {
      this.calculateResults();
    },
    concentration() {
      this.calculateResults();
    },
  },
  data() {
    return {
      calculatorLabels,
      labels,
      weight: 0,
      doseForWeightUnit: null,
      doseForWeight: 0,
      concentrationUnit: null,
      concentration: 0,
      response: null,
    };
  },
  mounted() {
    if (this.calculator || this.modified) {
      this.setupVariables();
      this._setupScreenSizes(this);
    }
  },
  created: async function () {
    if (this.patient) {
      this.weight = `${this.patient.weight}`.replace(".", ",");
    }
    if (
      !this.modified &&
      (!this.calculator ||
        this.calculator.id !== calculatorLabels.DOSE_CALCULATOR_ID)
    ) {
      await this.openCalculatorById({
        id: calculatorLabels.DOSE_CALCULATOR_ID,
        redirect: false,
      });
    }
  },
};
</script>
